/**
 * Route pathname helpers for conditional UI and for the edge middleware (keep
 * this file free of client-only and Node-only imports).
 */

// Legacy per-role sub-routes that still render a canvas with the in-page sidebar.
const LEGACY_CANVAS_ROUTE_PATTERN = /^\/[^/]+\/(canvas|view)(\/|$)/;
/**
 * A whole project id: the 25-char cuid v1 starting with `c` that
 * `src/middleware.ts` allowlists for `/:projectId` (keep the two in step).
 */
export const PROJECT_ID_PATTERN = /^c[a-z0-9]{24}$/;

// Lev Mobile: `/m` (project picker) and `/{projectId}/m`, with any sub-page.
const MOBILE_ROUTE_PATTERN = new RegExp(
  `^(?:/${PROJECT_ID_PATTERN.source.slice(1, -1)})?/m(?:/|$)`,
);

/**
 * True on any Lev Mobile page. App-wide floating widgets check this: the
 * mobile shell's fixed bottom nav owns the bottom of the screen, so anything
 * pinned there covers a tab.
 */
export function isMobileRoute(pathname: string | null | undefined): boolean {
  if (pathname == null) return false;
  return MOBILE_ROUTE_PATTERN.test(pathname);
}

/**
 * Where the middleware sends a signed-out request for a protected page.
 *
 * Only Lev Mobile goes to `/sign-in` with the requested path kept, so a founder
 * who opens the phone app lands back in it after signing in. Every other path
 * keeps the original redirect to `/`: that is app-wide behavior, and mobile
 * ships behind the `mobile-app` flag, which the edge cannot evaluate.
 */
export function signedOutRedirectTarget(pathname: string, search: string): string {
  if (!isMobileRoute(pathname)) return "/";
  return `/sign-in?${new URLSearchParams({ redirect_url: pathname + search }).toString()}`;
}

// Unified project page: top-level `/{projectId}`. The optional trailing slash
// keeps `/${projectId}/` matching but rejects sub-pages.
const UNIFIED_PROJECT_ROUTE_PATTERN = new RegExp(
  `^/${PROJECT_ID_PATTERN.source.slice(1, -1)}/?$`,
);

/**
 * True when pathname is a project canvas route — the unified `/{projectId}`
 * page, or the legacy `/:projectId/canvas` / `/:projectId/view` sub-routes.
 * Used to hide the floating feedback button when the in-page sidebar (which
 * carries its own help submenu) is shown.
 */
export function isCanvasRoute(pathname: string | null | undefined): boolean {
  if (pathname == null) return false;
  return (
    LEGACY_CANVAS_ROUTE_PATTERN.test(pathname) ||
    UNIFIED_PROJECT_ROUTE_PATTERN.test(pathname)
  );
}

// Cross-project logged-in shell routes that mount `LoggedInShell` +
// `HomeSidebar`. The sidebar carries its own help submenu with "Give
// feedback" and a dev-indicator wrench, so the floating PostHog widget
// and the Next.js dev indicator should hide here just like on the canvas.
const LOGGED_IN_SHELL_ROUTE_PATTERN =
  /^\/(?:|orbit(?:\/.*)?|founder-apps(?:\/.*)?)\/?$/;

/**
 * True when pathname is one of the logged-in shell routes that mount
 * `LoggedInShell` + `HomeSidebar` (`/`, `/orbit`, `/founder-apps` and
 * their nested paths). Used to hide the floating feedback button and
 * Next.js dev indicator on those routes — the sidebar provides
 * equivalent affordances.
 */
export function isLoggedInShellRoute(
  pathname: string | null | undefined,
): boolean {
  if (pathname == null) return false;
  return LOGGED_IN_SHELL_ROUTE_PATTERN.test(pathname);
}
